'use client'

import { ReactElement } from 'react'

import { useTranslations } from 'next-intl'
import ReactPaginate from 'react-paginate'

import ArticleBigItem from '@/components/site/ArticleBigItem'
import ArticleMinItem from '@/components/site/ArticleMinItem'
import Breadcrumbs from '@/components/site/Breadcrumbs'
import SurveyBanner from '@/components/site/SurveyBanner'
import { useRouter } from '@/i18n/navigation'
import { BlogPreview } from '@/services/blog.service'

import styles from './styles.module.scss'

interface Props {
    page: number
    totalPages: number
    list: BlogPreview[]
}

export default function BlogList({
    page,
    totalPages,
    list,
}: Props): ReactElement {
    const router = useRouter()
    const t = useTranslations('blog')
    const tCommon = useTranslations('common')

    return (
        <div className={styles.wrapper}>
            <Breadcrumbs
                items={[
                    {
                        title: tCommon('home'),
                        href: '/',
                    },
                    {
                        title: t('title'),
                    },
                ]}
            />
            <SurveyBanner />
            <div className={styles.outer}>
                <div className={styles.header}>
                    <h1 className={styles.title}>{t('title')}</h1>
                    <span className={styles.description}>{t('description')}</span>
                    {list.length === 0 ? (
                        <p className={styles.empty}>{t('empty')}</p>
                    ) : (
                        <div className={styles.row__big}>
                            {list
                                .filter((_, index) => index < 2)
                                .map(article => {
                                    const fullSlug = article.slug
                                    return (
                                        <ArticleBigItem
                                            key={fullSlug}
                                            title={article.title}
                                            coverImage={article.coverImage}
                                            link={`/blog/${fullSlug}`}
                                            excerpt={article.excerpt}
                                        />
                                    )
                                })}
                        </div>
                    )}
                </div>
                <div className={styles.row}>
                    {list
                        .filter((_, index) => index > 1)
                        .map(article => {
                            const fullSlug = article.slug
                            return (
                                <ArticleMinItem
                                    key={fullSlug}
                                    title={article.title}
                                    coverImage={article.coverImage}
                                    link={`/blog/${fullSlug}`}
                                    excerpt={article.excerpt}
                                />
                            )
                        })}
                </div>
            </div>
            {totalPages > 1 && (
                <ReactPaginate
                    initialPage={page - 1}
                    className={styles.pagination}
                    activeClassName={styles.pagination__active}
                    disabledClassName={styles.pagination__disabled}
                    breakLabel={'...'}
                    previousAriaLabel={t('prevPageAriaLabel')}
                    nextAriaLabel={t('nextPageAriaLabel')}
                    nextLabel={t('nextPage')}
                    hrefBuilder={pageIndex =>
                        pageIndex === 1 ? '/blog' : `/blog/page-${pageIndex}`
                    }
                    onPageChange={({ selected }) =>
                        router.push(
                            `/blog${selected > 0 ? `/page-${selected + 1}` : ''}`
                        )
                    }
                    pageRangeDisplayed={2}
                    marginPagesDisplayed={3}
                    pageCount={totalPages}
                    ariaLabelBuilder={pageNumber =>
                        t('pageAriaLabel', { page: pageNumber })
                    }
                    previousLabel={t('prevPage')}
                    prevRel={t('prevPage')}
                    nextRel={t('nextPage')}
                />
            )}
        </div>
    )
}
